import { useCallback, useEffect, useState } from 'react'

import type { AgentCommand } from '../api/admin.ts'
import { commands } from '../api/resources.ts'
import { COMMAND_POLL_MS, isAwaitingAgent } from './semantics.ts'

export interface CommandTracking {
  command: AgentCommand | null
  error: unknown
  /** Refaz a leitura agora (e retoma o acompanhamento, se ainda depende do agente). */
  refresh: () => void
}

/**
 * Acompanha UM comando por `GET /api/v1/commands/{id}`.
 *
 * Relê a cada ~3 s enquanto `state` ∈ {pending, delivered} — ainda depende do
 * agente — e para no primeiro estado terminal. A próxima leitura só é agendada
 * depois que a anterior volta: com servidor lento, nunca há duas em voo.
 *
 * `initial` (a resposta do POST) aparece de imediato, sem esperar a 1ª leitura.
 */
export function useCommandTracking(id: string, initial: AgentCommand | null = null): CommandTracking {
  const [command, setCommand] = useState<AgentCommand | null>(initial)
  const [error, setError] = useState<unknown>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined

    const read = (): void => {
      commands
        .get(id, controller.signal)
        .then((value) => {
          if (controller.signal.aborted) return
          setCommand(value)
          setError(null)
          if (isAwaitingAgent(value.state)) timer = setTimeout(read, COMMAND_POLL_MS)
        })
        .catch((cause: unknown) => {
          if (controller.signal.aborted) return
          // Sem retry automático (useAction.ts): o operador lê o erro e decide.
          setError(cause)
        })
    }

    read()

    return () => {
      controller.abort()
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [id, nonce])

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  return { command, error, refresh }
}
